import { UpperCasePipe } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ProviderDataReview } from 'app/core/models/provider-data-review.model';
import { ProviderDataReviewAssignmentsComponent } from '../provider-data-review-assignments/provider-data-review-assignments.component';
import { ProviderDataReviewsService } from 'app/core/services/provider-data-reviews.service';
import { NavigationService } from 'app/core/services/navigation.service';
import { BlankValueDashPipe } from 'app/shared/pipes/blank-value-dash.pipe';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { Provider } from 'app/core/models/provider.model';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { ProviderService } from 'app/core/services/provider.service';
import { ModelEventsService, ModelEventsSubscriptionSet } from 'app/core/services/model-events.service';

const humanizedReviewTypeMapping = {
  'ProviderFaxNumberDataReview': 'fax number data review',
  'ProviderSpecialtyDataReview': 'specialty data review',
};

interface ProviderRemoteEventProviderDataReviewUpdated {
  type: "provider_data_review_updated",
}

type ProviderRemoteEvent =
  ProviderRemoteEventProviderDataReviewUpdated;

@Component({
    selector: 'app-provider-data-review',
    templateUrl: './provider-data-review.component.html',
    styleUrls: ['./provider-data-review.component.scss'],
    imports: [
      FaIconComponent,
      UpperCasePipe,
      BlankValueDashPipe,
      ProviderDataReviewAssignmentsComponent,
    ]
})

export class ProviderDataReviewComponent implements OnInit, OnDestroy {
  private providerDataReviewService = inject(ProviderDataReviewsService);
  private providerService = inject(ProviderService);
  private navigationService = inject(NavigationService);
  private authService = inject(AuthenticationService);
  private modelEventsService = inject(ModelEventsService);

  @Input() provider: Provider;

  providerDataReview: ProviderDataReview;
  canManageProviderData = false;
  editingAssignedTo = false;

  humanizedReviewTypeMapping = humanizedReviewTypeMapping;

  remoteEventsSubscriptionSet: ModelEventsSubscriptionSet<ProviderRemoteEvent>;
  remoteEventsSubscription: Subscription;

  ngOnInit() {
    this.loadProviderDataReview();
    this.canManageProviderData = this.authService.hasPermissionTo(UserRoleName.manageProviderData);
    this.setupRemoteEventsSubscriptionSet();
  }

  ngOnDestroy() {
    this.remoteEventsSubscriptionSet.unsubscribeAll();

    if (this.remoteEventsSubscription) {
      this.remoteEventsSubscription.unsubscribe();
    }
  }

  onEditAssignedTo($event) {
    $event.preventDefault();
    this.editingAssignedTo = true;
  }

  onAssignedToCancel() {
    this.editingAssignedTo = false;
  }

  onAssignedToUpdate(assignedToId) {
    if (assignedToId !== this.providerDataReview.assignedTo?.id ) {
      this.providerDataReviewService.assignTo(this.providerDataReview, assignedToId).subscribe((_result) => {
        this.navigationService.reloadCurrentUrl();
      });

    } else {
      this.editingAssignedTo = false;
    }
  }

  get canAssignToProviderDataReview() {
    return this.canManageProviderData;
  }

  private loadProviderDataReview() {
    this.providerDataReview = this.getOpenProviderDataReview(this.provider);
  }

  private getOpenProviderDataReview(provider) {
    return provider.providerDataReviews.find( r => r.status === 'open' );
  }

  private setupRemoteEventsSubscriptionSet() {
    this.remoteEventsSubscriptionSet = this.modelEventsService.newSet<ProviderRemoteEvent>();

    this.remoteEventsSubscription =
      this.remoteEventsSubscriptionSet.events.subscribe(([_modelName, modelId, event]) => {
        this.handleRemoteEvent(modelId, event);
      });

    this.remoteEventsSubscriptionSet.subscribe('Provider', this.provider.id);
  }

  private handleRemoteEvent(modelId, event) {
    if (modelId === this.provider.id && event.type === 'provider_data_review_updated') {
      this.providerService.get(this.provider.id).subscribe(provider => {
        const openReview = this.getOpenProviderDataReview(provider);
        if (openReview) {
          // only change it out if the open review changed for
          // the provider.
          if (openReview.id !== this.providerDataReview?.id) {
            this.providerDataReview = openReview;
          }
        } else {
          this.providerDataReview = null;
        }
      })
    }
  }
}